import * as Dialog from '@radix-ui/react-dialog'
import { ChatLauncher } from '../ChatLauncher.jsx'
import { ChatPanel } from '../ChatPanel.jsx'
import { useChatLayout } from '../../../context/ChatLayoutContext.jsx'
import { cn } from '../../../lib/cn.js'
import { OverlaySplitSurface } from './OverlaySplitSurface.jsx'

/**
 * Centered modal overlay. Uses Radix Dialog so focus trap, ESC-to-close,
 * and `aria-modal` are correct without us implementing them. Radix
 * portals default to `document.body`, but inside a Shadow DOM that
 * skips our `<style>` injection - we override the container with the
 * shadow root so portal'd content still inherits widget CSS.
 *
 * Desktop shell follows v1 `OverlayChatLayout`: wide paper (~1200px),
 * chat left / suggestions right; narrow viewports stack the two halves.
 */
export function OverlayLayout({
  conf,
  messages,
  isTyping,
  onSend,
  ochatbot,
  container,
  onLeadFormSubmit,
  onOrderStatusResult,
  ...panelRest
}) {
  const { open, toggleChat, openChat, closeChat } = useChatLayout()

  return (
    <Dialog.Root
      open={open}
      modal={false}
      onOpenChange={(next) => (next ? openChat() : closeChat())}
    >
      <ChatLauncher
        open={open}
        onToggle={toggleChat}
        label={conf?.widgetTitle || 'Chat with us'}
        conf={conf}
      />
      <Dialog.Portal container={container}>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 z-[2147483645] bg-black/50',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=open]:fade-in data-[state=closed]:fade-out',
          )}
        />
        <Dialog.Content
          aria-describedby={undefined}
          className={cn(
            'fixed left-1/2 top-1/2 z-[2147483647] flex -translate-x-1/2 -translate-y-1/2 flex-col',
            'h-[100dvh] w-full max-h-[100dvh] max-w-[1200px] overflow-hidden rounded-none',
            'md:h-[min(620px,80vh)] md:w-[min(1200px,calc(100vw-2rem))] md:max-h-[620px] md:rounded-2xl',
            'border border-[var(--color-border)] bg-[var(--color-surface)]',
            'shadow-[var(--shadow-widget)]',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95',
          )}
        >
          <Dialog.Title className="sr-only">
            {conf?.widgetTitle || 'Chat'}
          </Dialog.Title>
          <OverlaySplitSurface
            conf={conf}
            messages={messages}
            onClose={closeChat}
            onSend={onSend}
            ochatbot={ochatbot}
            onLeadFormSubmit={onLeadFormSubmit}
            onOrderStatusResult={onOrderStatusResult}
          >
            <ChatPanel
              {...panelRest}
              conf={conf}
              messages={messages}
              isTyping={isTyping}
              onSend={onSend}
              onClose={closeChat}
              ochatbot={ochatbot}
              className={cn(
                'min-h-0 flex-1 border-0 rounded-none',
                panelRest.className,
              )}
              embedSuggestions={false}
              showClose={false}
              headerClassName="pr-14"
              onLeadFormSubmit={onLeadFormSubmit}
              onOrderStatusResult={onOrderStatusResult}
            />
          </OverlaySplitSurface>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
